// Menu.jsx
import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { X, ChevronDown } from 'lucide-react';
import logo from '../../assets/navbar_logo.png';

const Menu = ({ isMenuOpen, navLinks, projectSections, onMenuToggle, onPopupOpen, scrollToSection }) => {
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);

  const handleLogoClick = () => {
    onMenuToggle(false);
  };

  const handleSiteVisitClick = () => {
    onPopupOpen();
    onMenuToggle(false);
  };

  const handleMobileDropdownClick = (sectionId) => {
    scrollToSection(sectionId);
    onMenuToggle(false);
    setIsMobileDropdownOpen(false);
  };

  const handleNavLinkClick = (hasDropdown) => {
    if (!hasDropdown) {
      onMenuToggle(false);
    }
  };

  return (
    <>
      {/* Backdrop Overlay */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 lg:hidden z-40 ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => onMenuToggle(false)}
      />

      {/* Sliding Menu Panel - Full Width */}
      <div 
        className={`fixed top-0 right-0 h-full w-full bg-white transition-transform duration-300 ease-in-out lg:hidden z-50 ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Menu Header with Logo and Close Button */}
          <div className="flex items-center justify-between px-3 py-4 border-b border-gray-200">
            <Link 
              to='/' 
              onClick={handleLogoClick}
            >
              <img 
                src={logo} 
                alt="Logo" 
                className="h-10 w-auto"
              />
            </Link>
            <button
              onClick={() => onMenuToggle(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              aria-label="Close menu"
            >
              <X size={28} className="text-gray-700" />
            </button>
          </div>

          {/* Menu Items - Centered */}
          <div className="flex-1 overflow-y-auto px-6 py-8">
            <div className="space-y-2">
              {navLinks.map((link, index) => (
                link.hasDropdown ? (
                  <div key={link.name}>
                    <div
                      className={`flex items-center justify-between px-6 py-4 rounded-lg text-lg font-medium transition-all duration-200 transform text-gray-700 hover:text-[var(--primarycolor)] hover:bg-gray-50 cursor-pointer ${
                        isMenuOpen 
                          ? 'translate-x-0 opacity-100' 
                          : 'translate-x-4 opacity-0'
                      }`}
                      style={{ 
                        transitionDelay: isMenuOpen ? `${index * 50}ms` : '0ms' 
                      }}
                    >
                      <NavLink 
                        to={link.href}
                        onClick={(e) => {
                          if (isMobileDropdownOpen) {
                            e.preventDefault();
                            setIsMobileDropdownOpen(false);
                          } else {
                            onMenuToggle(false);
                          }
                        }}
                        className={({ isActive }) =>
                          `flex-1 ${isActive ? 'text-[var(--primarycolor)]' : ''}`
                        }
                      >
                        {link.name}
                      </NavLink>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMobileDropdownOpen(!isMobileDropdownOpen);
                        }}
                        className="p-2 -mr-2"
                      >
                        <ChevronDown 
                          size={20} 
                          className={`transition-transform duration-200 ${isMobileDropdownOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                    </div>
                    
                    {/* Mobile Dropdown */}
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${
                        isMobileDropdownOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="pl-4 space-y-1 mt-1">
                        {projectSections.map((section) => (
                          <button
                            key={section.id}
                            onClick={() => handleMobileDropdownClick(section.id)}
                            className="w-full text-left px-6 py-3 rounded-lg text-base font-medium text-gray-600 hover:text-[var(--primarycolor)] hover:bg-gray-50 transition-all duration-150"
                          >
                            {section.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <NavLink
                    key={link.name}
                    to={link.href}
                    className={({ isActive }) =>
                      `block px-6 py-4 rounded-lg text-lg font-medium transition-all duration-200 transform ${
                        isActive
                          ? 'bg-gray-100 text-[var(--primarycolor)]'
                          : 'text-gray-700 hover:text-[var(--primarycolor)] hover:bg-gray-50'
                      } ${
                        isMenuOpen 
                          ? 'translate-x-0 opacity-100' 
                          : 'translate-x-4 opacity-0'
                      }`
                    }
                    style={{ 
                      transitionDelay: isMenuOpen ? `${index * 50}ms` : '0ms' 
                    }}
                    onClick={() => handleNavLinkClick(link.hasDropdown)}
                  >
                    {link.name}
                  </NavLink>
                )
              ))}
            </div>
          </div>

          {/* Menu Footer with CTA */}
          <div className="px-6 py-6 border-t border-gray-200">
            <button 
              onClick={handleSiteVisitClick}
              className={`w-full bg-[var(--primarycolor)] hover:bg-red-800 text-white px-6 py-4 rounded-lg text-lg font-semibold transition-all duration-200 hover:shadow-lg transform ${
                isMenuOpen 
                  ? 'translate-x-0 opacity-100' 
                  : 'translate-x-4 opacity-0'
              }`}
              style={{ 
                transitionDelay: isMenuOpen ? `${navLinks.length * 50}ms` : '0ms' 
              }}
            >
              Book a Site Visit
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Menu;